"use client";

import axios from "axios";
import {useEffect, useState} from 'react';
import {SubmitHandler, useForm} from "react-hook-form";
import {z} from "zod";
import {zodResolver} from "@hookform/resolvers/zod";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {Check, ChevronsUpDown, Loader2} from "lucide-react";
import {cn} from "@/lib/utils.ts";
import {useToast} from "@/components/ui/use-toast.ts";
import {Button} from "@/components/ui/button.tsx";
import {Input} from "@/components/ui/input.tsx";
import {Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger} from '@/components/ui/dialog';
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/components/ui/select";
import {Form, FormControl, FormField, FormItem, FormLabel, FormMessage} from "@/components/ui/form.tsx";
import {Popover, PopoverContent, PopoverTrigger} from "@/components/ui/popover.tsx";
import {Command, CommandEmpty, CommandInput, CommandItem, CommandList} from "@/components/ui/command.tsx";
import useDebounce from "@/hooks/use-debounce.tsx";
import {UserRole} from "@/types/UserRole.ts";
import {CreateUserDto, User} from "@/types/User.ts";
import {queryKeys} from "@/services/queryKeyFactory.ts";
import {createUser, updateUser} from "@/services/userService.ts";
import {sendWelcomeEmail} from "@/services/emailService.ts";
import {fetchLastDateToSubmitPreferences} from "@/services/settingsService.ts";
import {useTranslation} from "react-i18next";

interface UserFormProps {
    isOpen: boolean;
    onOpenChange: (value: boolean) => void;
    user: User | null;
}

type CloudDirectoryUser = {
    emails: { value: string }[];
    name: { givenName: string; familyName: string };
};

const UserForm = ({isOpen, onOpenChange, user}: UserFormProps) => {
    const {t} = useTranslation();
    const {t: vt} = useTranslation("validation");
    const [token, setToken] = useState('');
    const [query, setQuery] = useState('');
    const [cloudDirectoryUsers, setCloudDirectoryUsers] = useState<CloudDirectoryUser[]>([]);
    const debouncedQuery = useDebounce(query, 500);
    const {toast} = useToast();
    const queryClient = useQueryClient();

    const formSchema = z.object({
        firstName: z
            .string()
            .trim().min(1, {
                message: vt("required", {fieldName: t("user.firstName")}),
            }),
        lastName: z
            .string()
            .trim().min(1, {
                message: vt("required", {fieldName: t("user.lastName")}),
            }),
        email: z
            .string({
                required_error: vt("required", {fieldName: t("user.emailAddress")}),
            })
            .email(),
        role: z.nativeEnum(UserRole, {
            required_error: vt("required", {fieldName: t("common.role")})
        }),
    });

    useEffect(() => {
        const fetchToken = async () => {
            const apiKey = import.meta.env.VITE_IBM_API_KEY;
            if (!apiKey || apiKey === "undefined") {
                console.warn(
                    "VITE_IBM_API_KEY is not set. Rebuild the front-end with build-arg VITE_IBM_API_KEY=your_key to enable Cloud Directory user search."
                );
                return;
            }
            const url = "/iam/identity/token";
            try {
                const response = await axios.post(
                    url,
                    new URLSearchParams({
                        grant_type: "urn:ibm:params:oauth:grant-type:apikey",
                        apikey: apiKey,
                    }),
                    {
                        headers: {
                            "Content-Type": "application/x-www-form-urlencoded",
                            Accept: "application/json",
                        },
                    }
                );
                setToken(response.data.access_token);
            } catch (error) {
                console.error("Error fetching IAM token:", error);
            }
        };

        fetchToken();
    }, []);

    useEffect(() => {
        const fetchUsers = async () => {
            const tenantID = import.meta.env.VITE_TENANT_ID;
            const url = `/ibm/management/v4/${tenantID}/cloud_directory/Users?query=${debouncedQuery}`;

            try {
                if (token && debouncedQuery) {
                    const response = await axios.get(url, {
                        headers: {
                            'accept': 'application/json',
                            'authorization': `Bearer ${token}`
                        }
                    });
                    setCloudDirectoryUsers(response.data.Resources);
                }
            } catch (error) {
                console.error("Error fetching IBM CLoud Directory Users");
            }
        };

        fetchUsers();
    }, [debouncedQuery, token]);

    const {data: submitPreferenceDeadline} = useQuery({
        queryKey: queryKeys.fetchSettings.lastDateToSubmitPreferences,
        queryFn: () => fetchLastDateToSubmitPreferences(),
        initialData: new Date(new Date().setFullYear(new Date().getFullYear() + 1))
    });

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        mode: 'onChange',
    });

    const onCreateSuccess = async (newUser: User) => {
        await queryClient.setQueryData(queryKeys.fetchUsers.all, (oldData?: User[]) => {
            if (oldData) {
                return [newUser, ...oldData];
            }
            return [newUser];
        });

        let description = t("user.createSuccess");

        if (newUser.role !== UserRole.ADMIN) {
            try {
                await sendWelcomeEmail(newUser.role, newUser.email, newUser.fullName, submitPreferenceDeadline.toDateString());
                description += t("user.welcomeEmailSuccess");
            } catch (error) {
                description += t("user.welcomeEmailFailed");
                console.error('Error sending email:', error);
            }
        }

        toast({
            description: description,
        });
        onOpenChange(false);
    };

    const onUpdateSuccess = async (updatedUser: User) => {
        await queryClient.setQueryData(queryKeys.fetchUsers.all, (oldData?: User[]) => {
            if (oldData) {
                return oldData.map((user) =>
                    user.id === updatedUser.id ? updatedUser : user
                );
            }
            return [updatedUser];
        });
        toast({
            description: t("user.updateSuccess"),
        });
        onOpenChange(false);
    };

    const onRequestError = (error: Error) => {
        toast({
            variant: 'destructive',
            title: t("common.errorTitle"),
            description: error.message,
        });
    };

    const createMutation = useMutation({
        mutationFn: createUser,
        onSuccess: onCreateSuccess,
        onError: onRequestError,
    });

    const updateMutation = useMutation({
        mutationFn: ({id, ...dto}: CreateUserDto & { id: number }) => updateUser(id, dto),
        onSuccess: onUpdateSuccess,
        onError: onRequestError,
    });

    useEffect(() => {
        if (user) {
            form.reset({
                firstName: user.firstName,
                lastName: user.lastName,
                email: user.email,
                role: user.role,
            });
        } else {
            form.reset({
                firstName: "",
                lastName: "",
                email: undefined,
                role: undefined,
            });
        }
    }, [isOpen, user]);

    const onSubmit: SubmitHandler<z.infer<typeof formSchema>> = (values: z.infer<typeof formSchema>) => {
        const createDto: CreateUserDto = {
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            role: values.role,
        };
        if (!user) {
            createMutation.mutate(createDto);
        } else {
            updateMutation.mutate({...createDto, id: user.id});
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onOpenChange}>
            <DialogTrigger asChild>
                <Button size="sm">{t("common.add")}</Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
                <DialogHeader className="pb-2">
                    <DialogTitle>{user ? t("user.update") : t("user.create")}</DialogTitle>
                </DialogHeader>
                <Form {...form}>
                    <form className="space-y-4">
                        <FormField
                            control={form.control}
                            name="email"
                            render={({field}) => (
                                <FormItem className="flex flex-col">
                                    <FormLabel>{t("user.emailAddress")}</FormLabel>
                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <FormControl>
                                                <Button
                                                    variant="outline"
                                                    role="combobox"
                                                    className={cn(
                                                        "justify-between",
                                                        !field.value && "text-muted-foreground"
                                                    )}
                                                >
                                                    {field.value ?? t("user.select")}
                                                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50"/>
                                                </Button>
                                            </FormControl>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-96 p-0">
                                            <Command>
                                                <CommandInput placeholder={t("user.search")}
                                                              onChangeCapture={(e) => setQuery(e.currentTarget.value)}/>
                                                <CommandEmpty>{t("user.notFound")}</CommandEmpty>
                                                <CommandList>
                                                    {cloudDirectoryUsers.map((user) => (
                                                        <CommandItem
                                                            value={user.emails[0].value}
                                                            key={user.emails[0].value}
                                                            onSelect={() => {
                                                                form.setValue("email", user.emails[0].value, {shouldDirty: true});
                                                                form.setValue("firstName", user.name.givenName);
                                                                form.setValue("lastName", user.name.familyName);
                                                            }}
                                                        >
                                                            <Check
                                                                className={cn(
                                                                    "mr-2 h-4 w-4",
                                                                    user.emails[0].value === field.value
                                                                        ? "opacity-100"
                                                                        : "opacity-0"
                                                                )}
                                                            />
                                                            {user.emails[0].value}
                                                        </CommandItem>
                                                    ))}
                                                </CommandList>
                                            </Command>
                                        </PopoverContent>
                                    </Popover>
                                    <FormMessage/>
                                </FormItem>
                            )}
                        />
                        <FormField
                            name="firstName"
                            control={form.control}
                            render={({field}) => (
                                <FormItem>
                                    <FormLabel>{t("user.firstName")}</FormLabel>
                                    <FormControl>
                                        <Input placeholder={t("user.enterFirstName")} {...field} />
                                    </FormControl>
                                    <FormMessage/>
                                </FormItem>
                            )}
                        />
                        <FormField
                            name="lastName"
                            control={form.control}
                            render={({field}) => (
                                <FormItem>
                                    <FormLabel>{t("user.lastName")}</FormLabel>
                                    <FormControl>
                                        <Input placeholder={t("user.enterLastName")} {...field} />
                                    </FormControl>
                                    <FormMessage/>
                                </FormItem>
                            )}
                        />
                        <FormField
                            name="role"
                            control={form.control}
                            render={({field}) => (
                                <FormItem>
                                    <FormLabel>{t("common.role")}</FormLabel>
                                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                                        <FormControl>
                                            <SelectTrigger>
                                                <SelectValue placeholder={t("user.selectRole")}/>
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            {Object.values(UserRole).map((role) => (
                                                <SelectItem key={role} value={role}>
                                                    {role.toUpperCase()}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <FormMessage/>
                                </FormItem>
                            )}
                        />
                    </form>
                </Form>
                <DialogFooter>
                    <Button
                        type="button"
                        disabled={!form.formState.isDirty || createMutation.isPending} // could also use form.formState.isSubmitting
                        onClick={form.handleSubmit(onSubmit)}
                    >
                        {createMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin"/>}
                        {t("common.confirm")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default UserForm;
